import { ColumnDef } from '@tanstack/react-table'
import { OverflownText } from 'components/OverflownText'
import { useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import styled from 'styled-components'
import { MdOutlineCancel } from 'react-icons/md'
import { BiCommentX } from 'react-icons/bi'
import { Control, useForm } from 'react-hook-form'

import { Dialog, DialogContent } from 'components/UI/Modal/ModalPrimitive'
import { ActionButtons, DeleteIcon } from 'components/Buttons/ActionButtons'
import { ClientDataTable } from 'components/ClientDataTable/ClientDataTable'
import { DataTableContainer } from 'components/DataTable/UI'
import { DateCell } from 'components/Tables/CommonTableCells/DateCell'
import { Modal } from 'components/UI/Modal/Modal'
import useApiService from 'services/api'
import FbButton from 'components/FbUI/FbButton'
import {
  CompanyEmailExtensionRequests,
  CompanyEmailExtensionRequestsStatus,
  CompanyEmailExtensionRequestsStatusMap,
} from 'models/companies'
import { TextareaInput } from 'components/FormUtils/TextareaInput'
import { TextInput } from 'components/FormUtils/TextInput'

import * as S from './styles'
import { toast } from 'react-toastify'

export function CompanyDomainsTable({ disabled }: { disabled?: boolean }) {
  const api = useApiService()
  const { data, isLoading, refetch, isFetching } =
    api.useGetCompanyDomainRequests()
  const [domainIdToDelete, setDomainIdToDelete] = useState<number>()
  const [showModal, setShowModal] = useState<boolean>(false)

  const handleRowDelete = async () => {
    if (!domainIdToDelete) return

    try {
      await api.cancelCompanyDomainRequest(domainIdToDelete)
      setDomainIdToDelete(undefined)
      toast.success('Request deleted successfully')
    } catch (err) {
      return
    }

    await refetch()
  }

  const columns = useMemo<ColumnDef<CompanyEmailExtensionRequests, any>[]>(
    () => [
      {
        header: 'Domain',
        cell: ({ row }) => {
          return <span className="italic">{row.original.email_extension}</span>
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          if (
            row.original.status === CompanyEmailExtensionRequestsStatus.DENIED
          ) {
            return (
              <OverflownText tooltipText={row.original.admin_reason ?? ''}>
                <StatusBadge value={row.original.status} />
              </OverflownText>
            )
          }

          return <StatusBadge value={row.original.status} />
        },
      },

      {
        accessorKey: 'note',
        header: 'Note',
        cell: ({ row }) => {
          return <>{row.original.note || '-'}</>
        },
      },
      {
        accessorKey: 'created',
        header: 'Date Created',
        cell: DateCell,
      },
      {
        accessorKey: 'modified',
        header: 'Date Modified',
        cell: DateCell,
      },
      {
        id: 'edit',
        header: 'Actions',
        meta: { rightAlign: true },
        cell: ({ row }) => {
          const isNotPending =
            row.original.status !== CompanyEmailExtensionRequestsStatus.PENDING
          const isActionDisabled = disabled || isNotPending
          return (
            <ActionButtons
              className={'justify-end pr-0'}
              onDelete={
                isActionDisabled
                  ? () => {}
                  : () => setDomainIdToDelete(row.original.id)
              }
              deleteIcon={() => (
                <div
                  title={
                    disabled
                      ? 'You do not have permission to delete domain requests'
                      : isNotPending
                        ? 'You can only delete a pending request'
                        : 'Delete domain request'
                  }
                >
                  <DeleteIcon
                    icon={MdOutlineCancel}
                    onClick={
                      isActionDisabled
                        ? () => {}
                        : () => setDomainIdToDelete(row.original.id)
                    }
                    style={{
                      cursor: isActionDisabled ? 'not-allowed' : 'pointer',
                      opacity: isActionDisabled ? 0.3 : 1,
                    }}
                  />
                </div>
              )}
            />
          )
        },
      },
    ],
    [data, disabled]
  )

  const { control, setError, handleSubmit, reset } =
    useForm<CompanyEmailExtensionRequestsForm>({
      defaultValues: {
        email_extension: '',
        note: '',
      },
      mode: 'onSubmit',
    })

  const onSubmitCreate = handleSubmit(
    async (
      data: Pick<CompanyEmailExtensionRequests, 'email_extension' | 'note'>
    ) => {
      try {
        await api.postCompanyDomain(data)
      } catch (err: any) {
        if (err.response) {
          setError('email_extension', {
            message: err.response.data.email_extension,
          })
        }
        return
      }

      await refetch()
      setShowModal(false)
      reset()
    }
  )

  if (isLoading || !data || isFetching) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '500px',
        }}
      >
        <Spinner animation="border" />
      </div>
    )
  }

  const descriptionText = `
  All domains associated with the company.
  `

  return (
    <DataTableContainer>
      <ClientDataTable
        tableHeader={
          <S.SettingsTableHeader>
            <S.TableDescriptionText>
              <OverflownText
                tooltipText={descriptionText}
                maxLines={4}
                placement="bottom"
              >
                {descriptionText}
              </OverflownText>
            </S.TableDescriptionText>
            <FbButton onClick={() => setShowModal(true)} disabled={disabled}>
              Add Domain
            </FbButton>
          </S.SettingsTableHeader>
        }
        data={data || []}
        columns={columns}
      />
      <Modal
        open={!!domainIdToDelete}
        title={'Delete domain'}
        onOpenChange={(open) => !open && setDomainIdToDelete(undefined)}
        onAccept={() => handleRowDelete()}
        acceptButtonText={'Confirm'}
      >
        <span>Are you sure you want to delete this domain request?</span>
      </Modal>
      <DomainModal
        show={showModal}
        handleClose={() => {
          setShowModal(false)
          reset()
        }}
        control={control}
        onSubmit={onSubmitCreate}
      />
    </DataTableContainer>
  )
}

type CompanyEmailExtensionRequestsForm = Pick<
  CompanyEmailExtensionRequests,
  'email_extension' | 'note'
>

export function DomainModal({
  show,
  control,
  handleClose,
  onSubmit,
}: {
  show: boolean
  handleClose: () => void
  control: Control<CompanyEmailExtensionRequestsForm, any>
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>
}) {
  return (
    <Dialog open={show} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent id={'fb-modal-content'} className="px-4 max-w-sm">
        <h1 className="mb-4 text-2xl text-gray-800 font-semibold">
          Adding new domain
        </h1>
        <form className="max-w-md mx-auto w-full" onSubmit={onSubmit}>
          <div className="flex flex-col gap-3">
            <div>
              <TextInput
                noMargin
                name="email_extension"
                control={control}
                type="text"
                label="Domain"
                placeholder="domain.com"
                required
              />
            </div>
            <div>
              <TextareaInput
                className="mt-0"
                name="note"
                control={control}
                type="text"
                label="Note"
                placeholder="Add some note (optional)"
              />
            </div>
            <div className="flex gap-3">
              <FbButton
                className="flex-1"
                variant="secondary"
                onClick={handleClose}
              >
                Cancel
              </FbButton>
              <FbButton className="flex-1" variant="primary" type="submit">
                Add
              </FbButton>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

type BadgeColors = {
  [key in CompanyEmailExtensionRequestsStatus]: BadgeType
}

const badgeColors: BadgeColors = {
  [CompanyEmailExtensionRequestsStatus.APPROVED]: {
    color: '#00876C',
    background: '#e6f3f1',
  },
  [CompanyEmailExtensionRequestsStatus.CANCELED]: {
    color: '#E08B20',
    background: '#fef6ec',
  },
  [CompanyEmailExtensionRequestsStatus.DENIED]: {
    color: '#D43D51',
    background: '#fbecee',
    icon: <BiCommentX />,
  },
  [CompanyEmailExtensionRequestsStatus.PENDING]: {
    color: '#005187',
    background: '#e6eff3',
  },
}

export function StatusBadge({
  value,
}: {
  value: CompanyEmailExtensionRequestsStatus
}) {
  const icon = badgeColors[value].icon

  return (
    <div className="flex">
      <Badge {...badgeColors[value]}>
        {value ? CompanyEmailExtensionRequestsStatusMap[value] : 'Unavailable'}{' '}
        {icon}
      </Badge>
    </div>
  )
}

type BadgeType = {
  color?: string
  background?: string
  icon?: React.JSX.Element
}

const Badge = styled.div<BadgeType>`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 90%;
  border-radius: 10px;
  padding: 2px 8px;
  font-weight: 500;
  color: ${({ color }) => (color ? color : '#959595')};
  background-color: ${({ background }) =>
    background ? background : 'inherit'};
`
